import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** Session-wide cache of section-render responses, keyed by request URL.
   * Values are Promises so an in-flight prefetch and a click share one fetch. */
  static #sectionCache = new Map();

  /** @type {string | undefined} The most recent click's request URL — guards
   * against a slower earlier response morphing over a newer selection. */
  #latestRequestUrl;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    // Warm the section cache before the click: hover on desktop, touchstart on
    // mobile (fires ~100ms before click). pointerover bubbles; pointerenter doesn't.
    this.addEventListener('pointerover', this.#prefetchVariant);
    this.addEventListener('touchstart', this.#prefetchVariant, { passive: true });
    this.#resizeObserver.observe(this);

    // A landing URL can select a nonexistent combination — e.g. a color-grid
    // link with ?option_values= for a color that doesn't exist in the default
    // size. Run the same correction used after variant-change fetches so the
    // size snaps to the nearest one that exists and the change flow re-renders
    // media, price and URL. No-op when the selection already resolves.
    requestAnimationFrame(() => this.correctUnresolvedSelection());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // If the color+option combo doesn't exist, fall back to the first available (or first existing) variant for that color.
    const variantId = selectedOption.dataset.variantId || null;
    const fallbackVariantId = selectedOption.dataset.firstAvailableOrFirstVariantId || null;
    const shouldUseFallback = !variantId && !!fallbackVariantId;

    let requestUrl;
    if (shouldUseFallback) {
      const productUrl = (selectedOption.dataset.connectedProductUrl || this.dataset.productUrl || '').split('?')[0];
      requestUrl = `${productUrl}?variant=${fallbackVariantId}`;
      const sectionParam = this.#productSectionParam(loadsNewProduct);
      if (sectionParam) requestUrl += `&${sectionParam}`;
    } else {
      requestUrl = this.buildRequestUrl(selectedOption, null, [], loadsNewProduct);
    }

    this.fetchUpdatedSection(requestUrl, morphElementSelector);

    const url = new URL(window.location.href);

    const effectiveVariantId = shouldUseFallback ? fallbackVariantId : variantId;
    if (document.querySelector('.frenzy-payment-buttons button') != null) {
      document.querySelector('.frenzy-payment-buttons button').setAttribute('data-id', variantId);
    }
    var modalContent = document.querySelector('product-form-component .shopify-product-form');
    if (modalContent) {
      var input = modalContent.querySelector('input[ref="variantId"], input[name="id"]');
      if (input) {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (isOnProductPage) {
      if (effectiveVariantId) {
        url.searchParams.set('variant', effectiveVariantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {boolean} [fullPage] - Force a full-page render (combined listing product switches).
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], fullPage = false) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    let viewParamValue = getViewParameterValue();
    if (!viewParamValue && (this.closest('.frenzy-modal') || this.closest('.frenzy-modal-content') || this.closest('#quick-add-dialog') || this.closest('.quick-add-modal'))) {
      viewParamValue = 'frenzy-quickview';
    }

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const sectionParam = this.#productSectionParam(fullPage);
    if (sectionParam) params.push(sectionParam);

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Builds a section_id param so product-page variant changes use the Section
   * Rendering API (renders only this product section) instead of a full-page
   * render. Returns null when the full page is required: combined-listing
   * product switches (updateMain morphs the whole <main>), alternate views,
   * and pickers living outside the main product section (cards, quick-add,
   * Frenzy modals — those already use SECTION_ID_MAP or need the view param).
   * @param {boolean} fullPage - Force a full-page render.
   * @returns {string | null} The section_id query param, or null.
   */
  #productSectionParam(fullPage) {
    if (fullPage) return null;
    if (getViewParameterValue()) return null;
    if (this.dataset.templateProductMatch !== 'true') return null;
    if (this.closest('product-card, quick-add-dialog, .frenzy-modal, .frenzy-modal-content, #quick-add-dialog, .quick-add-modal')) {
      return null;
    }

    const sectionId = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
    return sectionId ? `section_id=${sectionId}` : null;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // Cached/prefetched responses resolve instantly; the latest-URL guard
    // (rather than aborting) keeps rapid clicks from morphing stale HTML.
    this.#latestRequestUrl = requestUrl;

    this.#fetchSectionHtml(requestUrl)
      .then((responseText) => {
        if (this.#latestRequestUrl !== requestUrl) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
        if (typeof __st !== 'undefined' && __st.p == 'product' && typeof window.renderRecommendations === 'function') {
          requestAnimationFrame(() => window.renderRecommendations());
        }

        this.correctUnresolvedSelection();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Fetches (or reuses) the section HTML for a request URL. Failed fetches are
   * evicted so a retry hits the network again.
   * @param {string} requestUrl
   * @returns {Promise<string>}
   */
  #fetchSectionHtml(requestUrl) {
    let promise = VariantPicker.#sectionCache.get(requestUrl);
    if (!promise) {
      promise = fetch(requestUrl).then((response) => response.text());
      VariantPicker.#sectionCache.set(requestUrl, promise);
      promise.catch(() => VariantPicker.#sectionCache.delete(requestUrl));

      if (VariantPicker.#sectionCache.size > 24) {
        const oldest = VariantPicker.#sectionCache.keys().next().value;
        VariantPicker.#sectionCache.delete(oldest);
      }
    }
    return promise;
  }

  /**
   * Builds the same URL a click on this input would request, so the prefetch
   * warms the exact cache entry. Product-page section renders only.
   * @param {HTMLInputElement} input
   * @returns {string | null}
   */
  #buildPrefetchUrl(input) {
    const sectionParam = this.#productSectionParam(false);
    if (!sectionParam) return null;

    const productUrl = (input.dataset.connectedProductUrl || this.dataset.productUrl || '').split('?')[0];
    if (!productUrl) return null;

    // Mirror the fallback-variant branch in variantChanged()
    if (!input.dataset.variantId && input.dataset.firstAvailableOrFirstVariantId) {
      return `${productUrl}?variant=${input.dataset.firstAvailableOrFirstVariantId}&${sectionParam}`;
    }

    const selected = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    const inputGroup = input.closest('fieldset');
    const values = selected.map((el) => {
      return el.closest('fieldset') === inputGroup ? input.dataset.optionValueId : el.dataset.optionValueId;
    });
    if (values.length === 0 || values.some((value) => !value)) return null;

    return `${productUrl}?option_values=${values.join(',')}&${sectionParam}`;
  }

  /**
   * Prefetches the section render for a hovered/touched variant button.
   * @param {Event} event
   */
  #prefetchVariant = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('.variant-option__button-label');
    if (!label) return;

    const input = label.querySelector('input[type="radio"]');
    if (!input || input.checked || input.disabled) return;

    const url = this.#buildPrefetchUrl(input);
    if (url) this.#fetchSectionHtml(url);
  };

  /**
   * If the selection landed on a combination that doesn't exist (e.g. the user
   * switched to a color that isn't offered in the previously selected size),
   * auto-select the nearest size that DOES exist for the new color — even a
   * sold-out one — and re-run the normal change flow so a real variant
   * resolves and the media gallery, price and URL update.
   *
   * Nonexistent combos are detectable after the section re-render: the size
   * <option> is rendered `disabled` with an empty data-variant-id, while
   * sold-out-but-existing sizes keep their variant id. This also covers colors
   * past Liquid's 250-variant cap, where no fallback variant id is available
   * server-side at initial render.
   */
  correctUnresolvedSelection() {
    for (const select of this.querySelectorAll('select')) {
      const options = Array.from(select.options);
      const currentIndex = options.findIndex((option) => option.hasAttribute('selected'));
      const current = options[currentIndex];
      if (!current) continue;
      if (current.dataset.variantId && !current.disabled) continue;

      // Nearest existing size: search forward first (XS → S), then backward.
      const candidates = [...options.slice(currentIndex + 1), ...options.slice(0, Math.max(currentIndex, 0)).reverse()];
      const replacement = candidates.find((option) => option.dataset.variantId && !option.disabled);
      if (!replacement) continue;

      select.value = replacement.value;
      this.updateSelectedOption(select);
      select.dispatchEvent(new Event('change', { bubbles: true }));
      // One correction at a time — the follow-up render re-runs this check
      // and no-ops once the selection resolves, so this can't loop.
      return;
    }

    // Size radio buttons (the PDP replaced the size <select> with radios):
    // same correction, keyed off the server-rendered `checked` attribute.
    for (const fieldset of this.querySelectorAll('.variant-option__size-buttons')) {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const currentIndex = radios.findIndex((radio) => radio.hasAttribute('checked'));
      const current = radios[currentIndex];
      if (!current) continue;
      if (current.dataset.variantId && !current.disabled) continue;

      // Nearest existing size: search forward first (XS → S), then backward.
      const candidates = [...radios.slice(currentIndex + 1), ...radios.slice(0, Math.max(currentIndex, 0)).reverse()];
      const replacement = candidates.find((radio) => radio.dataset.variantId && !radio.disabled);
      if (!replacement) continue;

      replacement.checked = true;
      this.updateSelectedOption(replacement);
      replacement.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
